package com.evoapps.evoshift.domain.usecase.specialdays

import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.model.DaySpecials
import com.evoapps.evoshift.domain.model.LocalEvent
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.repository.LocalEventRepository
import com.evoapps.evoshift.domain.specialdays.SpecialDaysCalendar
import com.evoapps.evoshift.domain.specialdays.VoivodeshipFinder
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil

/** A period's special days by date, and the events typed in behind them (for editing). */
data class SpecialDaysSnapshot(
    val days: Map<LocalDate, DaySpecials> = emptyMap(),
    val events: List<LocalEvent> = emptyList()
)

/**
 * SH-227: each day's special days from [from] to [to], live: the workplace's events (always) and,
 * with the setting on, the long weekends, the summer holidays and the winter break of its
 * voivodeship (chosen, or read from the address; none when unknown).
 */
class ObserveSpecialDaysUseCase(private val repository: LocalEventRepository?) {

    operator fun invoke(workplace: Workplace?, from: LocalDate, to: LocalDate): Flow<SpecialDaysSnapshot> {
        if (workplace == null || to < from) return flowOf(SpecialDaysSnapshot())
        val automatic = workplace.settings.specialDaysEnabled
        val poland = workplace.settings.market == Market.POLAND
        val voivodeship = if (poland) VoivodeshipFinder.of(workplace)?.voivodeship else null
        val events = repository?.observeEvents(workplace.id, from, to) ?: flowOf(emptyList())
        return events.map { list ->
            SpecialDaysSnapshot(SpecialDaysCalendar.days(from..to, list, automatic, voivodeship, poland), list)
        }
    }
}

/** Why an event cannot be saved. */
enum class LocalEventProblem { NAME_MISSING, NAME_TOO_LONG, ENDS_BEFORE_START, TOO_LONG }

class LocalEventInvalidException(val problem: LocalEventProblem) : IllegalArgumentException(problem.name)

/**
 * SH-227: adds or changes an event typed in by an owner or a manager: the name (1–60 characters,
 * trimmed), its dates (at most [LocalEvent.MAX_DAYS] days) and whether it repeats every year; a new
 * one may go to several of the person's workplaces at once ([alsoIn]: the others, same id). The
 * rules allow it only to the workplace's owner and managers.
 */
class SaveLocalEventUseCase(private val repository: LocalEventRepository) {

    companion object {
        /** What stops [name] from [from] to [to] being saved (dates not given yet: only the name). */
        fun problemOf(name: String, from: LocalDate?, to: LocalDate?): LocalEventProblem? = when {
            name.isBlank() -> LocalEventProblem.NAME_MISSING
            name.trim().length > LocalEvent.MAX_NAME_LENGTH -> LocalEventProblem.NAME_TOO_LONG
            from == null || to == null -> null
            to < from -> LocalEventProblem.ENDS_BEFORE_START
            from.daysUntil(to) + 1 > LocalEvent.MAX_DAYS -> LocalEventProblem.TOO_LONG
            else -> null
        }
    }

    /** Saves a new event ([existing] null) or changes [existing] (its author and creation time stay). */
    suspend operator fun invoke(
        workplaceId: String,
        existing: LocalEvent?,
        name: String,
        from: LocalDate,
        to: LocalDate,
        yearly: Boolean,
        userId: String,
        alsoIn: List<String> = emptyList()
    ): Result<LocalEvent> {
        problemOf(name, from, to)?.let { return Result.failure(LocalEventInvalidException(it)) }
        val event = LocalEvent(
            id = existing?.id ?: repository.newId(),
            workplaceId = workplaceId,
            name = name.trim(),
            from = from,
            to = to,
            yearly = yearly,
            createdBy = existing?.createdBy?.takeIf { it.isNotEmpty() } ?: userId,
            createdAt = existing?.createdAt?.takeIf { it.isNotEmpty() } ?: AppClock.now().toString()
        )
        // Copies only for a new event: an edit changes this workplace's.
        val targets = listOf(workplaceId) + if (existing == null) alsoIn.filter { it != workplaceId }.distinct() else emptyList()
        return repository.saveEvent(event, targets).map { event }
    }
}

class DeleteLocalEventUseCase(private val repository: LocalEventRepository) {
    suspend operator fun invoke(workplaceId: String, eventId: String): Result<Unit> = repository.deleteEvent(workplaceId, eventId)
}
